/**
 * Tile chrome — header (drag handle in edit mode), title, kebab, edit-mode
 * remove button. TileFrame owns the ONE kebab menu; later slices extend it
 * through `extraMenuItems` (S6 Configure, S11 Open query/plan, S14 export) —
 * the C1-revised contract, replacing the ambiguous `onOpenMenu(anchor)`.
 * Body rendering is the caller's concern (`children`); the chrome adds the
 * freshness footer, stale dimming (data stays visible, unmistakably stale),
 * the body-state bridge (`TileChromeContext`), and single-tile Expand
 * (kebab item + title double-click → overlay dialog).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { GripVertical, MoreVertical, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { isQueryBackedTile, type DashboardTile } from '../types/dashboardTypes';
import { selectTileRuntime, useDashboardRuntimeStore } from '../store/dashboardRuntimeStore';
import { TileChromeContext, type TileChrome } from './tiles/tileChromeContext';
import { TileStatusBadge } from './tiles/TileStatusBadge';
import { TileFreshnessFooter } from './tiles/TileFreshnessFooter';
import { TileTemplateSubtitle } from './tiles/TileTemplateSubtitle';
import { TileExpandDialog } from './TileExpandDialog';

type TileFrameProps = {
  tile: DashboardTile;
  /** Enables the runtime status badge for query-backed tiles (S3). */
  dashboardId?: string;
  editMode: boolean;
  onRemove: () => void;
  onDuplicate: () => void;
  /** Extra DropdownMenuItem(s) rendered above Duplicate (S6/S11/S14 extension slot). */
  extraMenuItems?: ReactNode;
  children: ReactNode;
};

export function TileFrame({
  tile,
  dashboardId,
  editMode,
  onRemove,
  onDuplicate,
  extraMenuItems,
  children,
}: TileFrameProps) {
  // Radix's close-on-select is unreliable when item handlers kick off async
  // IPC work (S14 export items do) — an uncontrolled menu can linger as a
  // zombie whose eventual unmount RETURNS FOCUS to this trigger, dismissing
  // whatever popover is open at that moment (this broke the switcher in the
  // tall-dashboard PDF e2e). Controlled close fixes that — but the close
  // must be deferred with a MACROTASK: a capture-phase (or even microtask —
  // checkpoints run between listener callbacks) flush unmounts the content
  // mid-dispatch and the item's own onClick never fires (proven via probes:
  // capture ran, Configure's onClick didn't, its dialog never opened).
  const [menuOpen, setMenuOpen] = useState(false);
  const closeAfterDispatch = () => setTimeout(() => setMenuOpen(false), 0);
  const [expandOpen, setExpandOpen] = useState(false);
  const { track } = useAnalytics();

  const runtimeChrome = Boolean(dashboardId) && isQueryBackedTile(tile);
  const runtime = useDashboardRuntimeStore(selectTileRuntime(dashboardId ?? '', tile.id));
  // Finding 3 shape: a stale tile KEEPS its last successful render, dimmed,
  // with the always-visible amber freshness line + badge carrying the reason —
  // never a policy-copy replacement while data exists.
  const staleWithData = runtimeChrome && runtime?.status === 'stale' && Boolean(runtime.lastSuccess);
  const chrome = useMemo<TileChrome | null>(
    () => (dashboardId ? { dashboardId, tile } : null),
    [dashboardId, tile],
  );

  const openExpand = (via: 'menu' | 'title') => {
    track('feature:dashboard_tile_expand', { tileType: tile.tileType, via });
    setExpandOpen(true);
  };

  return (
    <div
      className="group/tile relative flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm"
      data-testid="dashboard-tile"
      data-tile-id={tile.id}
      data-tile-type={tile.tileType}
      data-stale={staleWithData || undefined}
    >
      <div
        className={`flex min-h-8 items-center gap-1.5 px-3 pt-2 ${
          editMode ? 'cursor-grab active:cursor-grabbing' : ''
        }`}
        data-drag-handle={editMode || undefined}
      >
        {editMode && (
          <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        )}
        <span
          className="truncate text-xs font-medium text-foreground"
          title={tile.title}
          data-testid="tile-title"
          // Double-click maximizes (edit mode keeps the header as a pure drag
          // surface, so the shortcut is browse-mode only; the kebab item works
          // in both).
          onDoubleClick={() => !editMode && dashboardId && openExpand('title')}
        >
          {tile.title}
        </span>
        {dashboardId && isQueryBackedTile(tile) && (
          <TileStatusBadge dashboardId={dashboardId} tileId={tile.id} />
        )}
        <span className="flex-1" />
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              className="rounded-sm p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/tile:opacity-100 data-[state=open]:opacity-100"
              title="Tile options"
              aria-label={`Options for tile ${tile.title}`}
              data-testid="tile-kebab-button"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClickCapture={closeAfterDispatch}>
            {dashboardId && (
              <DropdownMenuItem onClick={() => openExpand('menu')} data-testid="tile-expand-item">
                Expand tile
              </DropdownMenuItem>
            )}
            {extraMenuItems}
            <DropdownMenuItem onClick={onDuplicate} data-testid="tile-duplicate-item">
              Duplicate tile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onRemove}
              className="text-destructive focus:text-destructive"
              data-testid="tile-remove-item"
            >
              Remove tile
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {editMode && (
          // Trash icon, destructive-tinted at rest: an X here read like a
          // window-close affordance rather than "delete this tile".
          <button
            onClick={onRemove}
            className="rounded-sm p-1 text-destructive/70 hover:bg-destructive/10 hover:text-destructive"
            title="Remove tile"
            aria-label={`Remove tile ${tile.title}`}
            data-testid="tile-remove-button"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      {dashboardId && <TileTemplateSubtitle dashboardId={dashboardId} tile={tile} />}
      <div className={cn('min-h-0 flex-1 overflow-hidden', staleWithData && 'opacity-60')}>
        <TileChromeContext.Provider value={chrome}>{children}</TileChromeContext.Provider>
      </div>
      {/* Hidden in edit mode: the overlay would sit on (and, when stale,
          intercept clicks over) the grid wrapper's resize handle. */}
      {runtimeChrome && dashboardId && !editMode && (
        <TileFreshnessFooter dashboardId={dashboardId} tileId={tile.id} />
      )}
      {dashboardId && (
        <TileExpandDialog
          dashboardId={dashboardId}
          tile={tile}
          open={expandOpen}
          onOpenChange={setExpandOpen}
        />
      )}
    </div>
  );
}
